import React, { useEffect, useState } from 'react';
import { EventItem } from '../../types/event';
import { getEvents, deleteEvent } from '../../services/eventService';
import '../../styles/ApplicationsDashboard.css';
import '../../styles/EventsDashboard.css';
import EventCreateModal from './EventCreateModal';
import EventListItem from './EventListItem';
import ListToolbar from '../common/ListToolbar';
import { logger } from '../../utils/logger';
import { getEventFormPrefillData, shouldUseDevelopmentFeatures, loadDemoImage } from '../../utils/developmentUtils';

interface EventsDashboardProps {
  onViewApplications?: (eventId: string) => void;
}

/**
 * EventsDashboard component - Manages events listing, creation, editing, and deletion
 * Replaces the previous EventManagement component with improved UI and naming
 */
const EventsDashboard: React.FC<EventsDashboardProps> = ({ onViewApplications }) => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentEventId, setCurrentEventId] = useState<string | undefined>(undefined);
  const [form, setForm] = useState<Omit<EventItem, 'id'>>({
    title: '',
    date: '',
    imageUrl: '',
    address: '',
    description: '',
    startTime: { doorsOpen: '', doorsClose: '' },
    endTime: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'date' | 'name'>('date');
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('all');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEvents();
      setEvents(data);
      logger.success('✅ Events fetched successfully', { count: data.length });
    } catch (err) {
      logger.error('❌ Failed to fetch events:', err);
      setError('Failed to fetch events. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete event handler
  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }
    
    setSubmitting(true);
    try {
      await deleteEvent(id);
      logger.success('✅ Event deleted successfully', { id });
      
      // Update the events list
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      logger.error('❌ Failed to delete event:', err);
      alert('Failed to delete event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Filter, search, and sort events
  const now = new Date();
  const filteredEvents = events
    .filter(event => {
      if (filter === 'upcoming') return new Date(event.date) >= now;
      if (filter === 'past') return new Date(event.date) < now;
      return true;
    })
    .filter(event => event.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sort === 'date') {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      } else {
        return a.title.localeCompare(b.title);
      }
    });

  return (
    <div className="events-dashboard">
      <div className="dashboard-header">
        <h1>Events Dashboard</h1>
      </div>
      
      <div style={{ marginBottom: 20 }}>
        <ListToolbar
          filterOptions={[
            { label: 'All', value: 'all' },
            { label: 'Upcoming', value: 'upcoming' },
            { label: 'Past', value: 'past' },
          ]}
          filter={filter}
          setFilter={v => setFilter(v as typeof filter)}
          search={search}
          setSearch={setSearch}
          sortOptions={[
            { label: 'Sort by Date', value: 'date' },
            { label: 'Sort by Name', value: 'name' },
          ]}
          sort={sort}
          setSort={v => setSort(v as 'date' | 'name')}
        />
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="primary-button"
            style={{ minWidth: 170 }}
            onClick={async () => {
              // Reset edit mode and current event ID
              setEditMode(false);
              setCurrentEventId(undefined);
              
              // Check if we should prefill the form (only in local development with emulators)
              const prefillData = shouldUseDevelopmentFeatures() ? getEventFormPrefillData() : null;
              
              if (prefillData) {
                logger.info('🔧 Using prefilled data for event form (emulator mode only)');
                setForm(prefillData);
                
                // Load a demo image for the event
                const { file, previewUrl } = await loadDemoImage('/images/room_events.jpg', 'demo-event.jpg');
                setImageFile(file);
                setImagePreview(previewUrl);
              } else {
                setForm({
                  title: '',
                  date: '',
                  imageUrl: '',
                  address: '',
                  description: '',
                  startTime: { doorsOpen: '', doorsClose: '' },
                  endTime: '',
                });
                setImageFile(null);
                setImagePreview(null);
              }
              
              setFormError(null);
              setShowCreateModal(true);
            }}
          >
            Create New Event
          </button>
        </div>
      </div>
      <div className="applications-list">
        {loading && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading events...</p>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && filteredEvents.length === 0 && (
          <div className="no-applications">
            <p>No events found.</p>
          </div>
        )}
        {!loading && !error && filteredEvents.length > 0 && (
          <ul className="event-list">
            {filteredEvents.map(event => (
              <EventListItem 
                key={event.id}
                event={event}
                submitting={submitting}
                onViewApplications={onViewApplications}
                onEdit={(event) => {
                  // Pre-populate the form with the current event data
                  setForm({
                    title: event.title,
                    date: event.date,
                    imageUrl: event.imageUrl,
                    address: event.address,
                    description: event.description,
                    startTime: event.startTime,
                    endTime: event.endTime,
                  });
                  
                  // Set the image preview from the existing image URL
                  if (event.imageUrl) {
                    setImagePreview(event.imageUrl);
                  }
                  
                  // Set edit mode and current event ID
                  setEditMode(true);
                  setCurrentEventId(event.id);
                  
                  // Show the modal
                  setShowCreateModal(true);
                }}
                onDelete={handleDelete}
              />
            ))}
          </ul>
        )}
      </div>
      <EventCreateModal
        open={showCreateModal}
        form={form}
        setForm={setForm}
        imageFile={imageFile}
        setImageFile={setImageFile}
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
        formError={formError}
        setFormError={setFormError}
        submitting={submitting}
        setSubmitting={setSubmitting}
        editMode={editMode}
        eventId={currentEventId}
        onClose={() => {
          setShowCreateModal(false);
          setImageFile(null);
          setImagePreview(null);
          setForm({
            title: '',
            date: '',
            imageUrl: '',
            address: '',
            description: '',
            startTime: { doorsOpen: '', doorsClose: '' },
            endTime: '',
          });
          // Reset edit mode and current event ID
          setEditMode(false);
          setCurrentEventId(undefined);
        }}
        onSuccess={(newEvent) => {
          if (editMode && currentEventId) {
            // Update the existing event in the list
            setEvents(prev => prev.map(e => e.id === currentEventId ? newEvent : e));
            logger.success('✅ Event updated in list', { id: newEvent.id });
          } else {
            // Add the new event to the list
            setEvents(prev => [...prev, newEvent]);
            logger.success('✅ Event added to list', { id: newEvent.id });
          }
          
          // Reset form
          setForm({
            title: '',
            date: '',
            imageUrl: '',
            address: '',
            description: '',
            startTime: { doorsOpen: '', doorsClose: '' },
            endTime: '',
          });
          setImageFile(null);
          setImagePreview(null);
          
          // Reset edit mode and current event ID
          setEditMode(false);
          setCurrentEventId(undefined);
          
          // Close the modal
          setShowCreateModal(false);
        }}
      />
    </div>
  );
}

export default EventsDashboard;
